// All translatable copy for the MotoGO landing page.
// Keep this file as the single source of truth.

export const LANGUAGES = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "ro", name: "Română", flag: "🇷🇴" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "it", name: "Italiano", flag: "🇮🇹" },
  { code: "pt", name: "Português", flag: "🇵🇹" },
];

export const DEFAULT_LANG = "en";

export const TRANSLATIONS = {
  // ─────────────────────────────────────────────────────────  EN
  en: {
    nav: {
      features: "Features",
      app: "App",
      map: "Map",
      business: "Business",
      community: "Community",
      getTheApp: "Get the App",
      getApp: "Get App",
      openMenu: "Open menu",
      closeMenu: "Close menu",
      selectLanguage: "Select language",
    },
    hero: {
      pill: "MotoGO · The Rider's App",
      ride: "RIDE.",
      discover: "DISCOVER.",
      connect: "CONNECT.",
      subhead:
        "The premium motorcycle adventure app. Track live rides, discover hidden routes, and join a global community of riders — all in one beautifully crafted experience.",
      scroll: "Scroll",
      statRiders: "Riders Connected",
      statRoutes: "Routes Shared",
      statLive: "Live Rides",
      appStoreSub: "Download on the",
      appStoreMain: "App Store",
      playSub: "Get it on",
      playMain: "Google Play",
    },
    features: {
      eyebrow: "FEATURES",
      title1: "Built for the way",
      title2: "you actually ride.",
      subhead:
        "Six precision-crafted tools that turn every commute, weekend run, or cross-country adventure into something unforgettable.",
      f1: { title: "Live Map", desc: "See friends, events, fuel, and services nearby — plus weather and route alerts in real time.", badge: "LIVE" },
      f2: { title: "AI Route Generator", desc: "Type how many km you want to ride. Our AI builds you a route worth riding — instantly.", badge: "AI" },
      f3: { title: "Your Garage", desc: "Add your bike, track documents, and get reminders before insurance or ITP expires.", badge: "GARAGE" },
      f4: { title: "Maintenance Tips", desc: "Smart reminders for chain, tires, oil and brakes — ride with confidence, not guesswork.", badge: "CARE" },
      f5: { title: "Ride History", desc: "Every free ride and planned route, archived with distance, speed, and time — replayable any time.", badge: "ARCHIVE" },
      f6: { title: "Community Routes", desc: "Discover routes shared by riders near you — from short Sunday escapes to multi-day adventures.", badge: "SOCIAL" },
    },
    appPreview: {
      eyebrow: "APP PREVIEW",
      title1: "Crafted for your",
      title2: "tank, pocket, and journey.",
      subhead: "Every screen is built for one-glance clarity — even with gloves on, even at 120 km/h.",
      label: { liveMap: "Live Map", aiRoutes: "AI Routes", garage: "Garage", maintenance: "Maintenance", history: "Ride History" },
      hl1: { title: "Live Map", desc: "Friends, fuel, services & events near you." },
      hl2: { title: "AI Route Generator", desc: "Type a distance, get a ride worth taking." },
      hl3: { title: "Smart Care", desc: "Maintenance reminders & document tracking." },
    },
    map: {
      eyebrow: "INTERACTIVE MAP",
      title1: "Every road,",
      title2: "beautifully mapped.",
      subhead: "Pulse-live riders, glowing routes, and pinned legends — the MotoGO map turns navigation into something cinematic.",
      tabRoutes: "Routes",
      tabLive: "Live Riders",
      tabPins: "Pins",
      regionLabel: "Region",
      regionName: "North Ridge Loop",
      distance: "Distance",
      ridersLive: "Riders",
      ridersValue: "14 live",
      pace: "Pace",
      liveUpdated: "Live · Updated just now",
      startRide: "Start Live Ride",
      pins: { p1: "Alpine Pass", p2: "Coastal Run", p3: "Canyon Loop", p4: "Desert Edge", p5: "Misty Ridge" },
      live: "LIVE",
    },
    community: {
      eyebrow: "COMMUNITY",
      title1: "A global tribe",
      title2: "of riders.",
      subhead:
        "Find your crew, swap stories, and join meetups built by riders who get it. No noise. Just the road, the bike, and the people who love it as much as you do.",
      statRiders: "Active Riders",
      statCountries: "Countries",
      q1: { text: "It's the first app that made me feel like part of a real motorcycle tribe.", role: "Adventure Rider · Lisbon" },
      q2: { text: "The route discovery is wild. Found a pass I'd ridden past for years.", role: "Touring · Munich" },
      tag1: "Highway 1, USA",
      tag2: "Group Ride",
      tag3: "Sunset Gear",
    },
    motoHub: {
      eyebrow: "MOTOHUB · SOCIAL",
      badge: "NEW",
      title1: "Your ride,",
      title2: "your story.",
      subhead: "MotoHUB is the social heart of MotoGO. Post your best shots, share on-board videos, and join groups of riders who ride like you do.",
      f1: { title: "Post Photos", desc: "Share your bike, your gear and your best moments on the road." },
      f2: { title: "Share Videos", desc: "Upload on-board clips and ride reels for the whole community to see." },
      f3: { title: "Join Groups", desc: "Find local crews and themed groups — and ride together, on and off the road." },
      cta: "Explore MotoHUB",
    },
    forBusiness: {
      eyebrow: "FOR BUSINESSES",
      title1: "Are you a store?",
      title2: "Join our partners.",
      subhead: "Stores like UltraMoto are already inside MotoGO. Bring yours in too, show your gear to riders who care, and become part of their next adventure.",
      ctaIntegrate: "Integrate Your Store",
      ctaMessage: "Send us a message",
      status: "Currently onboarding partners",
      b1: { title: "Reach thousands of riders", desc: "Be seen by an active community of motorcycle owners who actually buy gear." },
      b2: { title: "Grow your brand", desc: "Featured placement, store profile, and rider-verified reviews — not just clicks." },
      b3: { title: "Be part of MotoGO", desc: "Join a curated marketplace built around real motorcycle culture, not generic e-commerce." },
      partnerLabel: "Our first partner",
      partnerName: "UltraMoto",
      partnerVisit: "Visit store",
    },
    download: {
      eyebrow: "DOWNLOAD",
      title1: "Start Your",
      title2: "Next Adventure.",
      subhead: "Free to download. Built for serious riders. Join a growing community of riders already mapping their next great road.",
      scan: "Scan to download",
      free: "Free · iOS · Android",
      b1: { title: "Privacy-first", desc: "You own your rides." },
      b2: { title: "Rider Verified", desc: "Real riders. No bots." },
      b3: { title: "Offline Routes", desc: "Ride without signal." },
    },
    footer: {
      tagline: "The premium motorcycle adventure app. Live map, AI routes, garage, maintenance and ride history — in one place.",
      gProduct: "Product",
      gCompany: "Company",
      gApps: "Apps",
      lFeatures: "Features",
      lApp: "App Preview",
      lMap: "Interactive Map",
      lDownload: "Download",
      lBusiness: "For Businesses",
      lCommunity: "Community",
      lContact: "Contact",
      lAppStore: "App Store",
      lPlay: "Google Play",
      lInstagram: "Instagram",
      copyright: "All rights reserved.",
      slogan1: "Made by riders,",
      slogan2: "for riders.",
    },
  },

  // ─────────────────────────────────────────────────────────  RO
  ro: {
    nav: {
      features: "Funcții",
      app: "Aplicație",
      map: "Hartă",
      business: "Business",
      community: "Comunitate",
      getTheApp: "Descarcă App",
      getApp: "Descarcă",
      openMenu: "Deschide meniul",
      closeMenu: "Închide meniul",
      selectLanguage: "Selectează limba",
    },
    hero: {
      pill: "MotoGO · Aplicația motociclistului",
      ride: "CONDU.",
      discover: "DESCOPERĂ.",
      connect: "CONECTEAZĂ-TE.",
      subhead:
        "Aplicația premium pentru aventurile pe motocicletă. Urmărește curse live, descoperă rute ascunse și alătură-te unei comunități globale de motocicliști — totul într-o experiență superb concepută.",
      scroll: "Derulează",
      statRiders: "Motocicliști conectați",
      statRoutes: "Rute distribuite",
      statLive: "Curse live",
      appStoreSub: "Descarcă pe",
      appStoreMain: "App Store",
      playSub: "Disponibil pe",
      playMain: "Google Play",
    },
    features: {
      eyebrow: "FUNCȚII",
      title1: "Construită pentru felul",
      title2: "în care chiar conduci.",
      subhead:
        "Șase instrumente concepute cu precizie care transformă orice drum, weekend sau aventură interstatală în ceva de neuitat.",
      f1: { title: "Hartă Live", desc: "Vezi prieteni, evenimente, benzinării și service-uri în apropiere — plus alerte de vreme și rută în timp real.", badge: "LIVE" },
      f2: { title: "Generator AI de rute", desc: "Scrie câți kilometri vrei să faci. AI-ul nostru îți construiește instant o rută pe care merită să o parcurgi.", badge: "AI" },
      f3: { title: "Garajul Tău", desc: "Adaugă motocicleta, urmărește documentele și primește mementouri înainte ca asigurarea sau ITP-ul să expire.", badge: "GARAJ" },
      f4: { title: "Sfaturi de Mentenanță", desc: "Mementouri inteligente pentru lanț, anvelope, ulei și frâne — condu cu încredere, nu pe ghicite.", badge: "ÎNTREȚINERE" },
      f5: { title: "Istoricul Curselor", desc: "Fiecare cursă liberă și rută planificată, arhivate cu distanță, viteză și timp — redabile oricând.", badge: "ARHIVĂ" },
      f6: { title: "Rute din Comunitate", desc: "Descoperă rute distribuite de motocicliști din apropiere — de la scurte evadări de duminică la aventuri de mai multe zile.", badge: "SOCIAL" },
    },
    appPreview: {
      eyebrow: "APLICAȚIA",
      title1: "Făcută pentru rezervor,",
      title2: "buzunar și călătorie.",
      subhead: "Fiecare ecran e gândit pentru claritate la o singură privire — chiar și cu mănuși, chiar și la 120 km/h.",
      label: { liveMap: "Hartă Live", aiRoutes: "Rute AI", garage: "Garaj", maintenance: "Mentenanță", history: "Istoric" },
      hl1: { title: "Hartă Live", desc: "Prieteni, benzinării, service-uri și evenimente lângă tine." },
      hl2: { title: "Generator AI de rute", desc: "Spui distanța, primești o cursă care merită făcută." },
      hl3: { title: "Întreținere Inteligentă", desc: "Mementouri de mentenanță și gestionarea documentelor." },
    },
    map: {
      eyebrow: "HARTĂ INTERACTIVĂ",
      title1: "Fiecare drum,",
      title2: "frumos cartografiat.",
      subhead: "Motocicliști în timp real, rute strălucitoare și locuri legendare marcate — harta MotoGO transformă navigația într-o experiență cinematică.",
      tabRoutes: "Rute",
      tabLive: "În Live",
      tabPins: "Repere",
      regionLabel: "Regiune",
      regionName: "Bucla Crestei de Nord",
      distance: "Distanță",
      ridersLive: "Motocicliști",
      ridersValue: "14 live",
      pace: "Ritm",
      liveUpdated: "Live · Actualizat acum",
      startRide: "Începe Cursa Live",
      pins: { p1: "Pasul Alpin", p2: "Drum de Coastă", p3: "Bucla Canionului", p4: "Marginea Deșertului", p5: "Creasta cu Ceață" },
      live: "LIVE",
    },
    community: {
      eyebrow: "COMUNITATE",
      title1: "Un trib global",
      title2: "de motocicliști.",
      subhead:
        "Găsește-ți echipa, schimbă povești și participă la întâlniri organizate de motocicliști care înțeleg. Fără zgomot. Doar drumul, motocicleta și oamenii care iubesc asta la fel de mult ca tine.",
      statRiders: "Motocicliști Activi",
      statCountries: "Țări",
      q1: { text: "Este prima aplicație care m-a făcut să mă simt parte dintr-un adevărat trib al motocicliștilor.", role: "Aventurier · Lisabona" },
      q2: { text: "Descoperirea rutelor e incredibilă. Am găsit un pas pe lângă care trecusem ani de zile.", role: "Touring · München" },
      tag1: "Autostrada 1, SUA",
      tag2: "Cursă în Grup",
      tag3: "Echipament la Apus",
    },
    motoHub: {
      eyebrow: "MOTOHUB · SOCIAL",
      badge: "NOU",
      title1: "Cursa ta,",
      title2: "povestea ta.",
      subhead: "MotoHUB este inima socială a MotoGO. Postează cele mai bune poze, împarte videoclipuri de pe motor și alătură-te grupurilor de rideri ca tine.",
      f1: { title: "Postează Poze", desc: "Arată-ți motorul, echipamentul și cele mai tari momente de pe șosea." },
      f2: { title: "Împarte Videoclipuri", desc: "Încarcă clipuri filmate de pe motor pentru toată comunitatea." },
      f3: { title: "Alătură-te Grupurilor", desc: "Găsește cluburi locale și grupuri tematice — și mergeți împreună." },
      cta: "Explorează MotoHUB",
    },
    forBusiness: {
      eyebrow: "PENTRU AFACERI",
      title1: "Ai un magazin?",
      title2: "Alătură-te partenerilor noștri.",
      subhead: "Magazine ca UltraMoto sunt deja în MotoGO. Adu-l și pe al tău, arată-ți echipamentele motocicliștilor care contează și devino parte din următoarea lor aventură.",
      ctaIntegrate: "Integrează Magazinul",
      ctaMessage: "Trimite-ne un mesaj",
      status: "Înrolăm parteneri acum",
      b1: { title: "Ajunge la mii de motocicliști", desc: "Fii văzut de o comunitate activă de posesori de motociclete care chiar cumpără echipament." },
      b2: { title: "Crește-ți brandul", desc: "Plasare evidențiată, profil de magazin și recenzii verificate de motocicliști — nu doar click-uri." },
      b3: { title: "Fii parte din MotoGO", desc: "Alătură-te unui marketplace curat, construit în jurul culturii reale a motocicletei, nu e-commerce generic." },
      partnerLabel: "Primul nostru partener",
      partnerName: "UltraMoto",
      partnerVisit: "Vizitează magazinul",
    },
    download: {
      eyebrow: "DESCARCĂ",
      title1: "Începe-ți",
      title2: "Următoarea Aventură.",
      subhead: "Descărcare gratuită. Construită pentru motocicliști serioși. Alătură-te unei comunități în creștere care își mapează deja următorul drum mare.",
      scan: "Scanează pentru descărcare",
      free: "Gratuit · iOS · Android",
      b1: { title: "Confidențialitate", desc: "Cursele îți aparțin." },
      b2: { title: "Motocicliști Verificați", desc: "Motocicliști reali. Fără boți." },
      b3: { title: "Rute Offline", desc: "Mergi și fără semnal." },
    },
    footer: {
      tagline: "Aplicația premium pentru aventurile pe motocicletă. Hartă live, rute AI, garaj, mentenanță și istoric — într-un singur loc.",
      gProduct: "Produs",
      gCompany: "Companie",
      gApps: "Aplicații",
      lFeatures: "Funcții",
      lApp: "Aplicație",
      lMap: "Hartă Interactivă",
      lDownload: "Descarcă",
      lBusiness: "Pentru Afaceri",
      lCommunity: "Comunitate",
      lContact: "Contact",
      lAppStore: "App Store",
      lPlay: "Google Play",
      lInstagram: "Instagram",
      copyright: "Toate drepturile rezervate.",
      slogan1: "Făcută de motocicliști,",
      slogan2: "pentru motocicliști.",
    },
  },

  // ─────────────────────────────────────────────────────────  FR
  fr: {
    nav: {
      features: "Fonctions",
      app: "App",
      map: "Carte",
      business: "Business",
      community: "Communauté",
      getTheApp: "Télécharger",
      getApp: "Télécharger",
      openMenu: "Ouvrir le menu",
      closeMenu: "Fermer le menu",
      selectLanguage: "Choisir la langue",
    },
    hero: {
      pill: "MotoGO · L'app des motards",
      ride: "ROULE.",
      discover: "DÉCOUVRE.",
      connect: "CONNECTE.",
      subhead:
        "L'application premium pour les aventures à moto. Suivez vos courses en direct, découvrez des itinéraires cachés et rejoignez une communauté mondiale de motards — le tout dans une expérience magnifiquement conçue.",
      scroll: "Défiler",
      statRiders: "Motards connectés",
      statRoutes: "Itinéraires partagés",
      statLive: "Courses en direct",
      appStoreSub: "Télécharger dans l'",
      appStoreMain: "App Store",
      playSub: "Disponible sur",
      playMain: "Google Play",
    },
    features: {
      eyebrow: "FONCTIONS",
      title1: "Conçue pour la façon",
      title2: "dont tu roules vraiment.",
      subhead:
        "Six outils conçus avec précision qui transforment chaque trajet, week-end ou aventure transcontinentale en moment inoubliable.",
      f1: { title: "Carte en direct", desc: "Vois tes amis, événements, stations et services à proximité — plus la météo et les alertes d'itinéraire en temps réel.", badge: "LIVE" },
      f2: { title: "Générateur de routes IA", desc: "Indique combien de km tu veux rouler. Notre IA te crée instantanément un itinéraire qui vaut le détour.", badge: "IA" },
      f3: { title: "Ton Garage", desc: "Ajoute ta moto, suis tes documents et reçois des rappels avant l'expiration de l'assurance.", badge: "GARAGE" },
      f4: { title: "Conseils d'entretien", desc: "Rappels intelligents pour chaîne, pneus, huile et freins — roule en confiance, pas au hasard.", badge: "ENTRETIEN" },
      f5: { title: "Historique des courses", desc: "Chaque balade et itinéraire archivés avec distance, vitesse et temps — rejouables à tout moment.", badge: "ARCHIVE" },
      f6: { title: "Itinéraires Communauté", desc: "Découvre les routes partagées par les motards près de toi — du dimanche court à l'aventure de plusieurs jours.", badge: "SOCIAL" },
    },
    appPreview: {
      eyebrow: "APERÇU DE L'APP",
      title1: "Conçue pour le réservoir,",
      title2: "la poche et le voyage.",
      subhead: "Chaque écran est pensé pour une lecture en un coup d'œil — même avec les gants, même à 120 km/h.",
      label: { liveMap: "Carte Live", aiRoutes: "Routes IA", garage: "Garage", maintenance: "Entretien", history: "Historique" },
      hl1: { title: "Carte en direct", desc: "Amis, carburant, services et événements près de toi." },
      hl2: { title: "Générateur de routes IA", desc: "Indique une distance, reçois une balade qui vaut le coup." },
      hl3: { title: "Entretien Malin", desc: "Rappels d'entretien et suivi des documents." },
    },
    map: {
      eyebrow: "CARTE INTERACTIVE",
      title1: "Chaque route,",
      title2: "magnifiquement cartographiée.",
      subhead: "Motards en direct, itinéraires lumineux et lieux légendaires épinglés — la carte MotoGO rend la navigation cinématique.",
      tabRoutes: "Itinéraires",
      tabLive: "En Direct",
      tabPins: "Points",
      regionLabel: "Région",
      regionName: "Boucle de Crête Nord",
      distance: "Distance",
      ridersLive: "Motards",
      ridersValue: "14 en direct",
      pace: "Allure",
      liveUpdated: "Live · Mis à jour à l'instant",
      startRide: "Démarrer en Direct",
      pins: { p1: "Col Alpin", p2: "Route Côtière", p3: "Boucle Canyon", p4: "Bord du Désert", p5: "Crête Brumeuse" },
      live: "LIVE",
    },
    community: {
      eyebrow: "COMMUNAUTÉ",
      title1: "Une tribu mondiale",
      title2: "de motards.",
      subhead:
        "Trouve ton équipe, échange tes histoires et rejoins les rassemblements créés par des motards qui comprennent. Pas de bruit. Juste la route, la moto et les gens qui l'aiment autant que toi.",
      statRiders: "Motards Actifs",
      statCountries: "Pays",
      q1: { text: "C'est la première app qui m'a fait sentir partie d'une vraie tribu de motards.", role: "Aventurière · Lisbonne" },
      q2: { text: "La découverte d'itinéraires est dingue. J'ai trouvé un col où j'étais passé pendant des années.", role: "Touring · Munich" },
      tag1: "Highway 1, USA",
      tag2: "Sortie en Groupe",
      tag3: "Équipement au Coucher",
    },
    motoHub: {
      eyebrow: "MOTOHUB · SOCIAL",
      badge: "NOUVEAU",
      title1: "Ta route,",
      title2: "ton histoire.",
      subhead: "MotoHUB est le cœur social de MotoGO. Publie tes meilleures photos, partage tes vidéos embarquées et rejoins des groupes de motards comme toi.",
      f1: { title: "Publier des Photos", desc: "Montre ta moto, ton équipement et tes meilleurs moments sur la route." },
      f2: { title: "Partager des Vidéos", desc: "Mets en ligne tes clips embarqués pour toute la communauté." },
      f3: { title: "Rejoindre des Groupes", desc: "Trouve des groupes locaux et thématiques — et roulez ensemble." },
      cta: "Découvrir MotoHUB",
    },
    forBusiness: {
      eyebrow: "POUR LES ENTREPRISES",
      title1: "Vous avez une boutique ?",
      title2: "Rejoignez nos partenaires.",
      subhead: "Des boutiques comme UltraMoto sont déjà dans MotoGO. Amenez la vôtre aussi, montrez votre équipement à des motards passionnés et devenez une étape de leur prochaine aventure.",
      ctaIntegrate: "Intégrer ma boutique",
      ctaMessage: "Envoyer un message",
      status: "Partenaires en cours d'intégration",
      b1: { title: "Touchez des milliers de motards", desc: "Soyez visible auprès d'une communauté active de propriétaires de motos qui achètent vraiment." },
      b2: { title: "Développez votre marque", desc: "Placement vedette, profil de boutique et avis vérifiés — pas juste des clics." },
      b3: { title: "Rejoignez MotoGO", desc: "Intégrez un marché curaté autour de la vraie culture moto, pas de l'e-commerce générique." },
      partnerLabel: "Notre premier partenaire",
      partnerName: "UltraMoto",
      partnerVisit: "Visiter la boutique",
    },
    download: {
      eyebrow: "TÉLÉCHARGER",
      title1: "Commence Ta",
      title2: "Prochaine Aventure.",
      subhead: "Téléchargement gratuit. Conçue pour les motards sérieux. Rejoins une communauté grandissante qui cartographie déjà sa prochaine grande route.",
      scan: "Scanner pour télécharger",
      free: "Gratuit · iOS · Android",
      b1: { title: "Confidentialité", desc: "Tes courses t'appartiennent." },
      b2: { title: "Motards Vérifiés", desc: "De vrais motards. Pas de bots." },
      b3: { title: "Itinéraires Hors-ligne", desc: "Roule sans réseau." },
    },
    footer: {
      tagline: "L'application premium pour les aventures à moto. Carte live, routes IA, garage, entretien et historique — en un seul endroit.",
      gProduct: "Produit",
      gCompany: "Société",
      gApps: "Apps",
      lFeatures: "Fonctions",
      lApp: "Aperçu",
      lMap: "Carte Interactive",
      lDownload: "Télécharger",
      lBusiness: "Entreprises",
      lCommunity: "Communauté",
      lContact: "Contact",
      lAppStore: "App Store",
      lPlay: "Google Play",
      lInstagram: "Instagram",
      copyright: "Tous droits réservés.",
      slogan1: "Fait par des motards,",
      slogan2: "pour des motards.",
    },
  },

  // ─────────────────────────────────────────────────────────  ES
  es: {
    nav: {
      features: "Funciones",
      app: "App",
      map: "Mapa",
      business: "Negocios",
      community: "Comunidad",
      getTheApp: "Descargar",
      getApp: "Descargar",
      openMenu: "Abrir menú",
      closeMenu: "Cerrar menú",
      selectLanguage: "Elegir idioma",
    },
    hero: {
      pill: "MotoGO · La app del motorista",
      ride: "CONDUCE.",
      discover: "DESCUBRE.",
      connect: "CONECTA.",
      subhead:
        "La app premium para aventuras en moto. Sigue rutas en directo, descubre caminos ocultos y únete a una comunidad global de motoristas — todo en una experiencia bellamente diseñada.",
      scroll: "Desplázate",
      statRiders: "Motoristas conectados",
      statRoutes: "Rutas compartidas",
      statLive: "Rutas en directo",
      appStoreSub: "Descarga en la",
      appStoreMain: "App Store",
      playSub: "Disponible en",
      playMain: "Google Play",
    },
    features: {
      eyebrow: "FUNCIONES",
      title1: "Hecha para cómo",
      title2: "conduces de verdad.",
      subhead:
        "Seis herramientas diseñadas con precisión que convierten cualquier trayecto, finde o aventura intercontinental en algo inolvidable.",
      f1: { title: "Mapa en directo", desc: "Mira amigos, eventos, gasolineras y servicios cercanos — además de alertas de tiempo y ruta en tiempo real.", badge: "LIVE" },
      f2: { title: "Generador de rutas IA", desc: "Escribe cuántos km quieres rodar. Nuestra IA te arma al instante una ruta que vale la pena.", badge: "IA" },
      f3: { title: "Tu Garaje", desc: "Añade tu moto, controla los documentos y recibe avisos antes de que caduque el seguro o la ITV.", badge: "GARAJE" },
      f4: { title: "Mantenimiento", desc: "Recordatorios inteligentes para cadena, neumáticos, aceite y frenos — rueda con confianza.", badge: "CUIDADO" },
      f5: { title: "Historial de rutas", desc: "Cada salida y ruta planeada, archivadas con distancia, velocidad y tiempo — reproducibles cuando quieras.", badge: "ARCHIVO" },
      f6: { title: "Rutas de la comunidad", desc: "Descubre rutas compartidas por motoristas cerca — desde escapadas de domingo hasta aventuras de varios días.", badge: "SOCIAL" },
    },
    appPreview: {
      eyebrow: "VISTA DE LA APP",
      title1: "Hecha para tu depósito,",
      title2: "bolsillo y viaje.",
      subhead: "Cada pantalla está pensada para entenderse de un vistazo — incluso con guantes, incluso a 120 km/h.",
      label: { liveMap: "Mapa Live", aiRoutes: "Rutas IA", garage: "Garaje", maintenance: "Mantenimiento", history: "Historial" },
      hl1: { title: "Mapa en directo", desc: "Amigos, gasolineras, servicios y eventos cerca de ti." },
      hl2: { title: "Generador de rutas IA", desc: "Escribe una distancia, recibe una ruta que vale la pena." },
      hl3: { title: "Cuidado Inteligente", desc: "Recordatorios y seguimiento de documentos." },
    },
    map: {
      eyebrow: "MAPA INTERACTIVO",
      title1: "Cada carretera,",
      title2: "bellamente trazada.",
      subhead: "Motoristas en directo, rutas luminosas y leyendas marcadas — el mapa MotoGO vuelve cinematográfica la navegación.",
      tabRoutes: "Rutas",
      tabLive: "En Directo",
      tabPins: "Puntos",
      regionLabel: "Región",
      regionName: "Bucle de la Cresta Norte",
      distance: "Distancia",
      ridersLive: "Motoristas",
      ridersValue: "14 en directo",
      pace: "Ritmo",
      liveUpdated: "Live · Actualizado ahora",
      startRide: "Iniciar Ruta en Directo",
      pins: { p1: "Paso Alpino", p2: "Ruta Costera", p3: "Bucle del Cañón", p4: "Borde del Desierto", p5: "Cresta Neblinosa" },
      live: "LIVE",
    },
    community: {
      eyebrow: "COMUNIDAD",
      title1: "Una tribu global",
      title2: "de motoristas.",
      subhead:
        "Encuentra tu grupo, comparte historias y únete a quedadas hechas por motoristas que entienden. Sin ruido. Solo la carretera, la moto y la gente que la quiere tanto como tú.",
      statRiders: "Motoristas Activos",
      statCountries: "Países",
      q1: { text: "Es la primera app que me ha hecho sentir parte de una tribu real de motoristas.", role: "Aventurera · Lisboa" },
      q2: { text: "El descubrimiento de rutas es brutal. Encontré un puerto por el que pasé durante años.", role: "Touring · Múnich" },
      tag1: "Highway 1, USA",
      tag2: "Ruta en Grupo",
      tag3: "Equipo al Atardecer",
    },
    motoHub: {
      eyebrow: "MOTOHUB · SOCIAL",
      badge: "NUEVO",
      title1: "Tu ruta,",
      title2: "tu historia.",
      subhead: "MotoHUB es el corazón social de MotoGO. Publica tus mejores fotos, comparte vídeos a bordo y únete a grupos de moteros como tú.",
      f1: { title: "Publicar Fotos", desc: "Muestra tu moto, tu equipo y tus mejores momentos en la carretera." },
      f2: { title: "Compartir Vídeos", desc: "Sube clips grabados en ruta para toda la comunidad." },
      f3: { title: "Unirse a Grupos", desc: "Encuentra grupos locales y temáticos — y rodad juntos." },
      cta: "Explorar MotoHUB",
    },
    forBusiness: {
      eyebrow: "PARA EMPRESAS",
      title1: "¿Tienes una tienda?",
      title2: "Únete a nuestros socios.",
      subhead: "Tiendas como UltraMoto ya están en MotoGO. Trae la tuya también, muestra tu equipamiento a motoristas que importan y forma parte de su próxima aventura.",
      ctaIntegrate: "Integra tu tienda",
      ctaMessage: "Envíanos un mensaje",
      status: "Incorporando socios ahora",
      b1: { title: "Llega a miles de motoristas", desc: "Sé visible para una comunidad activa de propietarios que de verdad compran equipo." },
      b2: { title: "Haz crecer tu marca", desc: "Posición destacada, perfil de tienda y reseñas verificadas — no solo clics." },
      b3: { title: "Forma parte de MotoGO", desc: "Únete a un marketplace curado en torno a la cultura motera real, no e-commerce genérico." },
      partnerLabel: "Nuestro primer socio",
      partnerName: "UltraMoto",
      partnerVisit: "Visitar tienda",
    },
    download: {
      eyebrow: "DESCARGAR",
      title1: "Empieza Tu",
      title2: "Próxima Aventura.",
      subhead: "Descarga gratuita. Hecha para motoristas serios. Únete a una comunidad creciente que ya está trazando su próximo gran camino.",
      scan: "Escanea para descargar",
      free: "Gratis · iOS · Android",
      b1: { title: "Privacidad", desc: "Tus rutas son tuyas." },
      b2: { title: "Motoristas Verificados", desc: "Reales. Sin bots." },
      b3: { title: "Rutas Offline", desc: "Rueda sin cobertura." },
    },
    footer: {
      tagline: "La app premium para aventuras en moto. Mapa live, rutas IA, garaje, mantenimiento e historial — en un solo lugar.",
      gProduct: "Producto",
      gCompany: "Empresa",
      gApps: "Apps",
      lFeatures: "Funciones",
      lApp: "Vista de la app",
      lMap: "Mapa Interactivo",
      lDownload: "Descargar",
      lBusiness: "Para Empresas",
      lCommunity: "Comunidad",
      lContact: "Contacto",
      lAppStore: "App Store",
      lPlay: "Google Play",
      lInstagram: "Instagram",
      copyright: "Todos los derechos reservados.",
      slogan1: "Hecha por motoristas,",
      slogan2: "para motoristas.",
    },
  },

  // ─────────────────────────────────────────────────────────  DE
  de: {
    nav: {
      features: "Funktionen",
      app: "App",
      map: "Karte",
      business: "Business",
      community: "Community",
      getTheApp: "App holen",
      getApp: "Holen",
      openMenu: "Menü öffnen",
      closeMenu: "Menü schließen",
      selectLanguage: "Sprache wählen",
    },
    hero: {
      pill: "MotoGO · Die Biker-App",
      ride: "FAHR.",
      discover: "ENTDECKE.",
      connect: "VERBINDE.",
      subhead:
        "Die Premium-App für Motorradabenteuer. Verfolge Fahrten in Echtzeit, entdecke versteckte Routen und tritt einer globalen Community von Bikern bei — alles in einem wunderschön gestalteten Erlebnis.",
      scroll: "Scrollen",
      statRiders: "Verbundene Biker",
      statRoutes: "Geteilte Routen",
      statLive: "Live-Fahrten",
      appStoreSub: "Laden im",
      appStoreMain: "App Store",
      playSub: "Erhältlich bei",
      playMain: "Google Play",
    },
    features: {
      eyebrow: "FUNKTIONEN",
      title1: "Gebaut für die Art,",
      title2: "wie du wirklich fährst.",
      subhead:
        "Sechs präzise gestaltete Werkzeuge, die jede Strecke, jedes Wochenende oder jede Cross-Country-Tour unvergesslich machen.",
      f1: { title: "Live-Karte", desc: "Sieh Freunde, Events, Tankstellen und Werkstätten in der Nähe — plus Wetter- und Routen-Alerts in Echtzeit.", badge: "LIVE" },
      f2: { title: "KI-Routengenerator", desc: "Sag, wie viele km du fahren willst. Unsere KI baut dir sofort eine Route, die sich lohnt.", badge: "KI" },
      f3: { title: "Deine Garage", desc: "Trag dein Bike ein, behalte Dokumente im Blick und bekomme Erinnerungen vor Versicherungsablauf.", badge: "GARAGE" },
      f4: { title: "Wartungs-Tipps", desc: "Smarte Erinnerungen für Kette, Reifen, Öl und Bremsen — fahr mit Vertrauen, nicht mit Vermutung.", badge: "PFLEGE" },
      f5: { title: "Fahrhistorie", desc: "Jede freie Fahrt und geplante Route, archiviert mit Strecke, Geschwindigkeit und Zeit — jederzeit abspielbar.", badge: "ARCHIV" },
      f6: { title: "Community-Routen", desc: "Entdecke Routen, die Biker in deiner Nähe teilen — vom Sonntagsausflug bis zur Mehrtages-Tour.", badge: "SOZIAL" },
    },
    appPreview: {
      eyebrow: "APP-VORSCHAU",
      title1: "Gemacht für Tank,",
      title2: "Tasche und Reise.",
      subhead: "Jeder Bildschirm ist auf einen Blick verständlich — auch mit Handschuhen, auch bei 120 km/h.",
      label: { liveMap: "Live-Karte", aiRoutes: "KI-Routen", garage: "Garage", maintenance: "Wartung", history: "Historie" },
      hl1: { title: "Live-Karte", desc: "Freunde, Tank, Services & Events in deiner Nähe." },
      hl2: { title: "KI-Routengenerator", desc: "Gib eine Distanz ein, bekomme eine lohnende Tour." },
      hl3: { title: "Smarte Pflege", desc: "Wartungserinnerungen & Dokumenten-Tracking." },
    },
    map: {
      eyebrow: "INTERAKTIVE KARTE",
      title1: "Jede Straße,",
      title2: "wunderschön kartiert.",
      subhead: "Live-Biker, leuchtende Routen und gepinnte Legenden — die MotoGO-Karte macht Navigation kinoreif.",
      tabRoutes: "Routen",
      tabLive: "Live-Biker",
      tabPins: "Pins",
      regionLabel: "Region",
      regionName: "Nordkamm-Schleife",
      distance: "Distanz",
      ridersLive: "Biker",
      ridersValue: "14 live",
      pace: "Tempo",
      liveUpdated: "Live · Gerade aktualisiert",
      startRide: "Live-Fahrt starten",
      pins: { p1: "Alpen-Pass", p2: "Küsten-Tour", p3: "Canyon-Schleife", p4: "Wüstenrand", p5: "Nebelkamm" },
      live: "LIVE",
    },
    community: {
      eyebrow: "COMMUNITY",
      title1: "Ein globaler Stamm",
      title2: "von Bikern.",
      subhead:
        "Finde deine Crew, tausche Geschichten und tritt Treffen bei, gemacht von Bikern, die es verstehen. Kein Lärm. Nur die Straße, das Bike und Leute, die es genauso lieben.",
      statRiders: "Aktive Biker",
      statCountries: "Länder",
      q1: { text: "Die erste App, die mich Teil eines echten Biker-Stamms fühlen ließ.", role: "Adventure-Riderin · Lissabon" },
      q2: { text: "Die Routenfindung ist wild. Habe einen Pass entdeckt, an dem ich jahrelang vorbeigefahren bin.", role: "Touring · München" },
      tag1: "Highway 1, USA",
      tag2: "Gruppenfahrt",
      tag3: "Sonnenuntergang-Gear",
    },
    motoHub: {
      eyebrow: "MOTOHUB · SOCIAL",
      badge: "NEU",
      title1: "Deine Fahrt,",
      title2: "deine Story.",
      subhead: "MotoHUB ist das soziale Herz von MotoGO. Poste deine besten Fotos, teile Onboard-Videos und tritt Gruppen von Fahrern wie dir bei.",
      f1: { title: "Fotos Posten", desc: "Zeig dein Bike, dein Gear und deine besten Momente auf der Straße." },
      f2: { title: "Videos Teilen", desc: "Lade Onboard-Clips für die ganze Community hoch." },
      f3: { title: "Gruppen Beitreten", desc: "Finde lokale Crews und Themengruppen — und fahrt gemeinsam." },
      cta: "MotoHUB Entdecken",
    },
    forBusiness: {
      eyebrow: "FÜR UNTERNEHMEN",
      title1: "Hast du einen Shop?",
      title2: "Werde einer unserer Partner.",
      subhead: "Shops wie UltraMoto sind schon in MotoGO. Bring deinen auch rein, zeig deine Ware Bikern, die zählen, und werde Teil ihres nächsten Abenteuers.",
      ctaIntegrate: "Shop integrieren",
      ctaMessage: "Nachricht senden",
      status: "Wir nehmen aktuell Partner auf",
      b1: { title: "Erreiche tausende Biker", desc: "Sei sichtbar in einer aktiven Community von Motorradbesitzern, die wirklich kaufen." },
      b2: { title: "Lass deine Marke wachsen", desc: "Hervorgehobene Platzierung, Shop-Profil und biker-verifizierte Bewertungen — nicht nur Klicks." },
      b3: { title: "Sei Teil von MotoGO", desc: "Tritt einem kuratierten Marktplatz bei, gebaut um echte Motorradkultur, kein generischer E-Commerce." },
      partnerLabel: "Unser erster Partner",
      partnerName: "UltraMoto",
      partnerVisit: "Shop besuchen",
    },
    download: {
      eyebrow: "DOWNLOAD",
      title1: "Starte Dein",
      title2: "Nächstes Abenteuer.",
      subhead: "Kostenlos. Für ernsthafte Biker gebaut. Tritt einer wachsenden Community bei, die schon ihre nächste große Strecke plant.",
      scan: "Zum Download scannen",
      free: "Gratis · iOS · Android",
      b1: { title: "Privatsphäre", desc: "Deine Fahrten gehören dir." },
      b2: { title: "Verifizierte Biker", desc: "Echte Biker. Keine Bots." },
      b3: { title: "Offline-Routen", desc: "Fahr ohne Signal." },
    },
    footer: {
      tagline: "Die Premium-App für Motorradabenteuer. Live-Karte, KI-Routen, Garage, Wartung und Fahrhistorie — an einem Ort.",
      gProduct: "Produkt",
      gCompany: "Unternehmen",
      gApps: "Apps",
      lFeatures: "Funktionen",
      lApp: "App-Vorschau",
      lMap: "Interaktive Karte",
      lDownload: "Download",
      lBusiness: "Für Unternehmen",
      lCommunity: "Community",
      lContact: "Kontakt",
      lAppStore: "App Store",
      lPlay: "Google Play",
      lInstagram: "Instagram",
      copyright: "Alle Rechte vorbehalten.",
      slogan1: "Von Bikern gemacht,",
      slogan2: "für Biker.",
    },
  },

  // ─────────────────────────────────────────────────────────  IT
  it: {
    nav: {
      features: "Funzioni",
      app: "App",
      map: "Mappa",
      business: "Aziende",
      community: "Community",
      getTheApp: "Scarica l'App",
      getApp: "Scarica",
      openMenu: "Apri menu",
      closeMenu: "Chiudi menu",
      selectLanguage: "Scegli la lingua",
    },
    hero: {
      pill: "MotoGO · L'app dei motociclisti",
      ride: "VAI.",
      discover: "SCOPRI.",
      connect: "CONNETTITI.",
      subhead:
        "L'app premium per le avventure in moto. Traccia le tue corse in tempo reale, scopri percorsi nascosti e unisciti a una community globale di motociclisti — tutto in un'esperienza splendidamente progettata.",
      scroll: "Scorri",
      statRiders: "Motociclisti connessi",
      statRoutes: "Percorsi condivisi",
      statLive: "Corse in diretta",
      appStoreSub: "Scarica su",
      appStoreMain: "App Store",
      playSub: "Disponibile su",
      playMain: "Google Play",
    },
    features: {
      eyebrow: "FUNZIONI",
      title1: "Costruita per come",
      title2: "guidi davvero.",
      subhead:
        "Sei strumenti pensati con precisione che trasformano ogni tragitto, weekend o avventura intercontinentale in qualcosa di indimenticabile.",
      f1: { title: "Mappa Live", desc: "Vedi amici, eventi, distributori e officine vicino — più meteo e avvisi di percorso in tempo reale.", badge: "LIVE" },
      f2: { title: "Generatore IA di percorsi", desc: "Scrivi quanti km vuoi fare. La nostra IA ti crea all'istante un percorso che vale.", badge: "IA" },
      f3: { title: "Il Tuo Garage", desc: "Aggiungi la moto, segui i documenti e ricevi promemoria prima della scadenza di assicurazione o revisione.", badge: "GARAGE" },
      f4: { title: "Manutenzione", desc: "Promemoria intelligenti per catena, gomme, olio e freni — guida con sicurezza, non a caso.", badge: "CURA" },
      f5: { title: "Storico Corse", desc: "Ogni uscita e percorso pianificato, archiviati con distanza, velocità e tempo — riproducibili sempre.", badge: "ARCHIVIO" },
      f6: { title: "Percorsi Community", desc: "Scopri percorsi condivisi da motociclisti vicino a te — dalle fughe di domenica alle avventure di più giorni.", badge: "SOCIAL" },
    },
    appPreview: {
      eyebrow: "ANTEPRIMA APP",
      title1: "Pensata per serbatoio,",
      title2: "tasca e viaggio.",
      subhead: "Ogni schermata è fatta per essere chiara con uno sguardo — anche con i guanti, anche a 120 km/h.",
      label: { liveMap: "Mappa Live", aiRoutes: "Percorsi IA", garage: "Garage", maintenance: "Manutenzione", history: "Storico" },
      hl1: { title: "Mappa Live", desc: "Amici, distributori, officine ed eventi vicino a te." },
      hl2: { title: "Generatore IA di percorsi", desc: "Dai una distanza, ricevi una corsa che vale la pena." },
      hl3: { title: "Cura Smart", desc: "Promemoria di manutenzione e gestione dei documenti." },
    },
    map: {
      eyebrow: "MAPPA INTERATTIVA",
      title1: "Ogni strada,",
      title2: "mappata splendidamente.",
      subhead: "Motociclisti in tempo reale, percorsi luminosi e luoghi leggendari segnati — la mappa MotoGO rende la navigazione cinematica.",
      tabRoutes: "Percorsi",
      tabLive: "In Diretta",
      tabPins: "Segnali",
      regionLabel: "Regione",
      regionName: "Anello del Crinale Nord",
      distance: "Distanza",
      ridersLive: "Motociclisti",
      ridersValue: "14 live",
      pace: "Ritmo",
      liveUpdated: "Live · Aggiornato ora",
      startRide: "Avvia Corsa Live",
      pins: { p1: "Passo Alpino", p2: "Strada Costiera", p3: "Anello del Canyon", p4: "Bordo del Deserto", p5: "Cresta Nebbiosa" },
      live: "LIVE",
    },
    community: {
      eyebrow: "COMMUNITY",
      title1: "Una tribù globale",
      title2: "di motociclisti.",
      subhead:
        "Trova la tua crew, scambia storie e partecipa ai raduni creati da motociclisti che capiscono. Niente rumore. Solo strada, moto e persone che la amano quanto te.",
      statRiders: "Motociclisti Attivi",
      statCountries: "Paesi",
      q1: { text: "È la prima app che mi ha fatto sentire parte di una vera tribù di motociclisti.", role: "Adventure Rider · Lisbona" },
      q2: { text: "La scoperta dei percorsi è pazzesca. Ho trovato un passo a cui sono passato accanto per anni.", role: "Touring · Monaco" },
      tag1: "Highway 1, USA",
      tag2: "Corsa in Gruppo",
      tag3: "Equipaggiamento al Tramonto",
    },
    motoHub: {
      eyebrow: "MOTOHUB · SOCIAL",
      badge: "NUOVO",
      title1: "La tua strada,",
      title2: "la tua storia.",
      subhead: "MotoHUB è il cuore social di MotoGO. Pubblica le tue foto migliori, condividi video a bordo e unisciti a gruppi di motociclisti come te.",
      f1: { title: "Pubblica Foto", desc: "Mostra la tua moto, il tuo equipaggiamento e i momenti migliori." },
      f2: { title: "Condividi Video", desc: "Carica clip girate in sella per tutta la community." },
      f3: { title: "Unisciti ai Gruppi", desc: "Trova crew locali e gruppi a tema — e guidate insieme." },
      cta: "Esplora MotoHUB",
    },
    forBusiness: {
      eyebrow: "PER LE AZIENDE",
      title1: "Hai un negozio?",
      title2: "Unisciti ai nostri partner.",
      subhead: "Negozi come UltraMoto sono già dentro MotoGO. Porta anche il tuo, mostra il tuo equipaggiamento a motociclisti che contano e diventa parte della loro prossima avventura.",
      ctaIntegrate: "Integra il tuo negozio",
      ctaMessage: "Inviaci un messaggio",
      status: "Stiamo inserendo partner",
      b1: { title: "Raggiungi migliaia di motociclisti", desc: "Sii visibile a una community attiva di proprietari che compra davvero." },
      b2: { title: "Fai crescere il tuo brand", desc: "Posizione in evidenza, profilo negozio e recensioni verificate — non solo click." },
      b3: { title: "Fai parte di MotoGO", desc: "Unisciti a un marketplace curato attorno alla vera cultura moto, non e-commerce generico." },
      partnerLabel: "Il nostro primo partner",
      partnerName: "UltraMoto",
      partnerVisit: "Visita il negozio",
    },
    download: {
      eyebrow: "SCARICA",
      title1: "Inizia la Tua",
      title2: "Prossima Avventura.",
      subhead: "Download gratuito. Pensata per motociclisti seri. Unisciti a una community in crescita che già mappa la sua prossima grande strada.",
      scan: "Scansiona per scaricare",
      free: "Gratuita · iOS · Android",
      b1: { title: "Privacy", desc: "Le tue corse sono tue." },
      b2: { title: "Motociclisti Verificati", desc: "Veri motociclisti. Niente bot." },
      b3: { title: "Percorsi Offline", desc: "Vai anche senza segnale." },
    },
    footer: {
      tagline: "L'app premium per le avventure in moto. Mappa live, percorsi IA, garage, manutenzione e storico — in un unico posto.",
      gProduct: "Prodotto",
      gCompany: "Azienda",
      gApps: "Apps",
      lFeatures: "Funzioni",
      lApp: "Anteprima",
      lMap: "Mappa Interattiva",
      lDownload: "Scarica",
      lBusiness: "Per le Aziende",
      lCommunity: "Community",
      lContact: "Contatti",
      lAppStore: "App Store",
      lPlay: "Google Play",
      lInstagram: "Instagram",
      copyright: "Tutti i diritti riservati.",
      slogan1: "Fatta da motociclisti,",
      slogan2: "per motociclisti.",
    },
  },

  // ─────────────────────────────────────────────────────────  PT
  pt: {
    nav: {
      features: "Funções",
      app: "App",
      map: "Mapa",
      business: "Negócios",
      community: "Comunidade",
      getTheApp: "Descarregar",
      getApp: "Descarregar",
      openMenu: "Abrir menu",
      closeMenu: "Fechar menu",
      selectLanguage: "Escolher idioma",
    },
    hero: {
      pill: "MotoGO · A app do motociclista",
      ride: "CONDUZ.",
      discover: "DESCOBRE.",
      connect: "CONECTA.",
      subhead:
        "A app premium para aventuras de mota. Acompanha viagens em direto, descobre rotas escondidas e junta-te a uma comunidade global de motociclistas — tudo numa experiência lindamente concebida.",
      scroll: "Desliza",
      statRiders: "Motociclistas conectados",
      statRoutes: "Rotas partilhadas",
      statLive: "Viagens em direto",
      appStoreSub: "Descarrega na",
      appStoreMain: "App Store",
      playSub: "Disponível no",
      playMain: "Google Play",
    },
    features: {
      eyebrow: "FUNÇÕES",
      title1: "Feita para a forma",
      title2: "como conduzes de verdade.",
      subhead:
        "Seis ferramentas pensadas ao pormenor que transformam qualquer trajeto, fim de semana ou aventura intercontinental em algo inesquecível.",
      f1: { title: "Mapa em direto", desc: "Vê amigos, eventos, postos e oficinas próximos — além de alertas de meteorologia e rota em tempo real.", badge: "LIVE" },
      f2: { title: "Gerador IA de rotas", desc: "Escreve quantos km queres fazer. A nossa IA cria-te na hora uma rota que vale a pena.", badge: "IA" },
      f3: { title: "A Tua Garagem", desc: "Adiciona a tua mota, gere documentos e recebe lembretes antes do seguro ou inspeção expirarem.", badge: "GARAGEM" },
      f4: { title: "Dicas de Manutenção", desc: "Lembretes inteligentes para corrente, pneus, óleo e travões — conduz com confiança, não na sorte.", badge: "CUIDADO" },
      f5: { title: "Histórico de Viagens", desc: "Cada passeio e rota planeada, arquivados com distância, velocidade e tempo — reproduzíveis a qualquer momento.", badge: "ARQUIVO" },
      f6: { title: "Rotas da Comunidade", desc: "Descobre rotas partilhadas por motociclistas perto de ti — de fugas curtas de domingo a aventuras de vários dias.", badge: "SOCIAL" },
    },
    appPreview: {
      eyebrow: "PRÉ-VISUALIZAÇÃO",
      title1: "Feita para o depósito,",
      title2: "o bolso e a viagem.",
      subhead: "Cada ecrã é pensado para se entender com um relance — mesmo com luvas, mesmo a 120 km/h.",
      label: { liveMap: "Mapa Live", aiRoutes: "Rotas IA", garage: "Garagem", maintenance: "Manutenção", history: "Histórico" },
      hl1: { title: "Mapa em direto", desc: "Amigos, postos, oficinas e eventos perto de ti." },
      hl2: { title: "Gerador IA de rotas", desc: "Dás uma distância, recebes uma viagem que vale a pena." },
      hl3: { title: "Cuidado Inteligente", desc: "Lembretes de manutenção e gestão de documentos." },
    },
    map: {
      eyebrow: "MAPA INTERATIVO",
      title1: "Cada estrada,",
      title2: "lindamente mapeada.",
      subhead: "Motociclistas em direto, rotas luminosas e locais lendários marcados — o mapa MotoGO torna a navegação cinematográfica.",
      tabRoutes: "Rotas",
      tabLive: "Em Direto",
      tabPins: "Pontos",
      regionLabel: "Região",
      regionName: "Circuito da Crista Norte",
      distance: "Distância",
      ridersLive: "Motociclistas",
      ridersValue: "14 em direto",
      pace: "Ritmo",
      liveUpdated: "Live · Atualizado agora",
      startRide: "Iniciar Viagem Live",
      pins: { p1: "Passo Alpino", p2: "Estrada Costeira", p3: "Circuito do Canyon", p4: "Limite do Deserto", p5: "Crista com Névoa" },
      live: "LIVE",
    },
    community: {
      eyebrow: "COMUNIDADE",
      title1: "Uma tribo global",
      title2: "de motociclistas.",
      subhead:
        "Encontra o teu grupo, troca histórias e participa em encontros criados por motociclistas que percebem. Sem ruído. Só a estrada, a mota e as pessoas que a amam tanto como tu.",
      statRiders: "Motociclistas Ativos",
      statCountries: "Países",
      q1: { text: "É a primeira app que me fez sentir parte de uma verdadeira tribo de motociclistas.", role: "Aventureira · Lisboa" },
      q2: { text: "A descoberta de rotas é brutal. Encontrei um passo por onde passei durante anos.", role: "Touring · Munique" },
      tag1: "Highway 1, EUA",
      tag2: "Viagem em Grupo",
      tag3: "Equipamento ao Pôr do Sol",
    },
    motoHub: {
      eyebrow: "MOTOHUB · SOCIAL",
      badge: "NOVO",
      title1: "A tua viagem,",
      title2: "a tua história.",
      subhead: "MotoHUB é o coração social do MotoGO. Publica as tuas melhores fotos, partilha vídeos a bordo e junta-te a grupos de motociclistas como tu.",
      f1: { title: "Publicar Fotos", desc: "Mostra a tua mota, o teu equipamento e os melhores momentos na estrada." },
      f2: { title: "Partilhar Vídeos", desc: "Carrega clips gravados na estrada para toda a comunidade." },
      f3: { title: "Juntar-se a Grupos", desc: "Encontra grupos locais e temáticos — e conduzam juntos." },
      cta: "Explorar MotoHUB",
    },
    forBusiness: {
      eyebrow: "PARA EMPRESAS",
      title1: "Tens uma loja?",
      title2: "Junta-te aos nossos parceiros.",
      subhead: "Lojas como a UltraMoto já estão na MotoGO. Traz a tua também, mostra o teu equipamento a motociclistas que importam e faz parte da próxima aventura deles.",
      ctaIntegrate: "Integra a tua loja",
      ctaMessage: "Envia-nos uma mensagem",
      status: "A integrar parceiros agora",
      b1: { title: "Chega a milhares de motociclistas", desc: "Sê visto por uma comunidade ativa de proprietários de motos que realmente compram equipamento." },
      b2: { title: "Faz crescer a tua marca", desc: "Destaque, perfil de loja e avaliações verificadas por motociclistas — não só cliques." },
      b3: { title: "Faz parte da MotoGO", desc: "Junta-te a um marketplace curado, construído à volta de cultura motociclista real, não e-commerce genérico." },
      partnerLabel: "O nosso primeiro parceiro",
      partnerName: "UltraMoto",
      partnerVisit: "Visitar loja",
    },
    download: {
      eyebrow: "DESCARREGAR",
      title1: "Começa a Tua",
      title2: "Próxima Aventura.",
      subhead: "Download gratuito. Feita para motociclistas a sério. Junta-te a uma comunidade em crescimento que já está a mapear a sua próxima grande estrada.",
      scan: "Digitaliza para descarregar",
      free: "Grátis · iOS · Android",
      b1: { title: "Privacidade", desc: "As tuas viagens são tuas." },
      b2: { title: "Motociclistas Verificados", desc: "Reais. Sem bots." },
      b3: { title: "Rotas Offline", desc: "Anda sem rede." },
    },
    footer: {
      tagline: "A app premium para aventuras de mota. Mapa live, rotas IA, garagem, manutenção e histórico — num só lugar.",
      gProduct: "Produto",
      gCompany: "Empresa",
      gApps: "Apps",
      lFeatures: "Funções",
      lApp: "Pré-visualização",
      lMap: "Mapa Interativo",
      lDownload: "Descarregar",
      lBusiness: "Para Empresas",
      lCommunity: "Comunidade",
      lContact: "Contacto",
      lAppStore: "App Store",
      lPlay: "Google Play",
      lInstagram: "Instagram",
      copyright: "Todos os direitos reservados.",
      slogan1: "Feita por motociclistas,",
      slogan2: "para motociclistas.",
    },
  },
};
